<script>
import moment from "moment";
import { mapGetters } from "vuex";
import Timeline from "./horizontal-timeline.vue";
import VerticalTimeline from "./vertical-timeline.vue";
import { filterSysComOut } from "../../config/comments.config";
import {
  adjustmentAccountingStep,
  adjustmentStep,
  allSteps,
  approvalStep,
  getCurrentStep,
  regularFPeAStep,
  reprovalStep,
  waitingInitialStep
} from "../../config/workflow.config";

/**
 * @typedef {Object} ParsedStep
 * @property {string} id - ID da etapa
 * @property {string} name - Nome da etapa
 * @property {string} [nextStep] - Próxima etapa
 * @property {boolean} completed - Se a etapa está completa
 * @property {boolean} current - Se é a etapa atual
 * @property {boolean} [repproved] - Se foi reprovada
 * @property {Array<Object>} comments - Comentários da etapa
 */

export default {
  components: {
    Modal: () => import("@/components/g-modal.vue"),
    Timeline,
    VerticalTimeline
  },
  props: {
    value: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      commentsModal: {
        opened: false
      },
      /** limite de etapas visiveis no card */
      maxStepsLength: 7
    };
  },
  computed: {
    ...mapGetters(["clientId"]),
    /**
     * Lista de comentários dos usuários
     * @type {import('../../config/comments.config').Comment[]}
     * @readonly
     */
    comments() {
      return this.value?.comentarios ?? [];
    },
    /**
     * @type {Array<ParsedStep>} firstParameters - Primeiras etapas (ocultas)
     * @readonly
     */
    firstParameters() {
      if (this.parameters.length < this.maxStepsLength) {
        return [];
      }

      return this.parameters.slice(0, -this.maxStepsLength);
    },
    /**
     * @type {Array<ParsedStep>} lastParameters - Últimas etapas (visíveis)
     * @readonly
     */
    lastParameters() {
      if (this.parameters.length <= this.maxStepsLength) {
        return this.parameters;
      }

      return this.parameters.slice(-this.maxStepsLength);
    },
    /**
     * @type {Array<ParsedStep>} parameters - Todas as etapas processadas
     * @readonly
     */
    parameters() {
      const comments = [...this.comments].sort((a, b) =>
        a.data?.localeCompare(b.data || "")
      );
      let current =
        getCurrentStep(this.value?.workflow?.nome ?? "") ?? waitingInitialStep;

      if (!comments.length) {
        const exclude = [
          adjustmentStep.id,
          regularFPeAStep.id,
          adjustmentAccountingStep.id,
          reprovalStep.id
        ];
        return allSteps
          .filter(({ id }) => !exclude.includes(id))
          .map((step, i) => ({
            ...step,
            completed: current.id === approvalStep.id,
            current: !i
          }));
      }

      const completedSteps = comments.map(comment => {
        const step = getCurrentStep(comment.workflow_nome ?? "") ?? {};
        return {
          ...step,
          completed: true,
          current: false,
          comments: [comment]
        };
      });

      if (
        completedSteps.length > 0 &&
        completedSteps.at(-1).id === current.id
      ) {
        completedSteps.pop();
      }

      const currentStep = {
        ...current,
        completed: current.id === approvalStep.id,
        current: true,
        repproved: current.id === reprovalStep.id,
        comments: []
      };

      const isReproved = current.id === reprovalStep.id;

      if (isReproved) {
        current = completedSteps.at(-1);
        current.completed = false;
        current.current = true;
        current.repproved = true;
      }

      const nextSteps = [];
      while (current.nextStep) {
        nextSteps.push(current.nextStep);
        current = current.nextStep;
      }

      const incompletedSteps = nextSteps.map(step => ({
        ...step,
        completed: false,
        current: false,
        comments: []
      }));

      const concatAndParseComments = (...items) => {
        let lastId = null;

        return items.reduceRight((acc, item) => {
          if (lastId !== item.id) {
            lastId = item.id;

            return [item, ...acc];
          }

          acc[0].comments.unshift(...item.comments);

          return acc;
        }, []);
      };

      if (isReproved) {
        incompletedSteps.splice(-1, 1, {
          ...reprovalStep,
          completed: true,
          current: true,
          repproved: true,
          comments: []
        });

        return concatAndParseComments(...completedSteps, ...incompletedSteps);
      }

      return concatAndParseComments(
        ...completedSteps,
        currentStep,
        ...incompletedSteps
      );
    },
    parsedDate() {
      const dateA = moment(this.value.data_inicio, "YYYY-MM-DD");
      const dateB = moment(this.value.data_fim, "YYYY-MM-DD");
      const parsed = [dateA, dateB].map(date =>
        date.isValid() ? date.format("DD/MM/YYYY") : "Data indefinida."
      );
      return parsed.join(" — ");
    },
    slaColor() {
      const status = this.value.sla_label;

      if (typeof status === "string") {
        if (status.toLowerCase() === "aprovado") {
          return "#6BB4BA";
        } else if (status.toLowerCase() === "atrasado") {
          return "#EC4C47";
        }
      }

      return null;
    },
    statusColor() {
      return this.value.sla_label === "Aprovado"
        ? "#6BB4BA"
        : this.value.sla_label === "Reprovado"
        ? "#FF4040"
        : "#FFC440";
    },
    userCommentsLength() {
      return this.comments.filter(filterSysComOut).length;
    }
  },
  methods: {
    goToBP() {
      this.$router.push({
        name: "ativacaoped-projetos-form-edicao",
        params: { id: this.value.id }
      });
    },
    showDots() {
      return this.firstParameters.length > 0;
    }
  }
};
</script>

<template>
  <v-card class="px-2" elevation="4">
    <div class="content-container">
      <div class="group">
        <div class="item">
          <h1>{{ value.nome }}</h1>
          <span>{{ parsedDate }}</span>
        </div>
        <div class="item">
          <h4>SLA</h4>
          <span :style="{ color: slaColor }">{{ value.sla_label }}</span>
        </div>
      </div>
      <div class="group">
        <div class="item">
          <Timeline
            :hiddenSteps="firstParameters.length"
            :visibleSteps="lastParameters"
          />
        </div>
      </div>
    </div>

    <div class="action-container">
      <div class="status" :style="{ color: statusColor }">
        <v-icon large>mdi-circle-small</v-icon>
        <span>{{ value.workflow.nome }}</span>
      </div>
      <div class="buttons">
        <v-badge
          color="secondary"
          :content="userCommentsLength"
          offset-y="16"
          offset-x="24"
          :value="userCommentsLength"
        >
          <v-btn color="secondary" text @click="commentsModal.opened = true">
            Ver todas etapas e comentários
          </v-btn>
        </v-badge>

        <v-btn color="secondary" depressed @click="goToBP">
          Visualizar Business Plan
        </v-btn>
      </div>
    </div>

    <Modal
      v-model="commentsModal.opened"
      hide-buttons
      title="Comentários"
      width="max-content"
    >
      <VerticalTimeline :steps="parameters" />
    </Modal>
  </v-card>
</template>

<style lang="scss" scoped>
@import "@/scss/_vuetify.scss";

.action-container,
.content-container {
  display: flex;
  flex-direction: row;
  padding: 1rem;
  width: 100%;
}

.action-container {
  align-items: center;
  justify-content: space-between;

  .status {
    span {
      color: inherit;
      font-size: 0.75rem;
      font-weight: 300;
    }

    .v-icon {
      color: inherit;
      font-size: 0.75rem;
    }
  }

  .v-btn {
    padding: 0.625rem 1rem;
    margin: 0 0.25rem;
  }
}

.content-container {
  border-bottom: thin solid #00000040;
  display: flex;
  flex-direction: row;

  @include md {
    flex-direction: column;
  }

  h1,
  h4 {
    font-size: 1.25rem;
    font-weight: 700;
    margin-bottom: 0.25rem;
  }

  h4 {
    font-size: 0.75rem;
  }

  span {
    color: #c5c7d0;
    font-size: 0.75rem;
  }

  .group {
    display: flex;
    flex-direction: column;
    width: 40%;
  }

  .group + .group {
    width: 60%;
  }

  .item {
    height: 100%;
  }

  .item + .item {
    margin-top: 0.5rem;
  }
}
</style>
